import { useState, useReducer, useCallback, useEffect, useMemo, useRef, useContext } from 'react';
import _ from 'underscore';
import {
  getImageAssetLimitPerAccount,
  GenerateImageContext,
  ROOT_FOLDER_ID,
  AIModelSource,
} from '@bingads-webui-campaign-react/ads-studio-app-shared-components';

import { detectBrandName } from './util';

export const statuses = {
  entry: 'entry',
  finished: 'finished',
  loading: 'loading',
  cancelling: 'cancelling',
};

export const actions = {
  search: 'search',
  finish: 'finish',
  cancel: 'cancel',
  fail: 'fail',
};

/* istanbul ignore next */
const reducer = (state, action) => {
  // action, [search, finish, cancel, failed]
  /* istanbul ignore switch */
  switch (state.status) {
    case statuses.entry:
    case statuses.finished:
      switch (action.type) {
        case actions.search:
          return ({ status: statuses.loading });
        default:
          break;
      }
      return ({ status: statuses.finished });
    case statuses.loading:
      switch (action.type) {
        case actions.cancel:
        case actions.fail:
          return ({ status: statuses.cancelling });
        case actions.finish:
          return ({ status: statuses.finished });
        case actions.search:
          return ({ status: statuses.loading });
        default:
          break;
      }
      return ({ status: statuses.finished });
    case statuses.cancelling:
      switch (action.type) {
        case actions.finish:
        case actions.fail:
          return ({ status: statuses.finished });
        default:
          break;
      }
      return ({ status: statuses.finished });
    default:
      return ({ status: statuses.finished });
  }
};

export const useGenerateImagePageState = ({
  dataService, i18n, logger, eventHandler, aid, notificationApi, folderId, perfMarker,
}) => {
  const log = useCallback((...args) => {
    if (logger) {
      logger.logTrace(...args);
    }
  }, [logger]);

  const {
    generateImageExample,
    setGenerateImageExample,
  } = useContext(GenerateImageContext);

  const clearExample = useCallback(() => {
    if (setGenerateImageExample) {
      setGenerateImageExample({
        prompt: undefined,
        imageUrl: undefined,
      });
    }
  }, [setGenerateImageExample]);

  useEffect(() => () => {
    clearExample();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [initialPrompt] = useState(generateImageExample && generateImageExample.prompt);
  const [prompt, setPrompt] = useState(initialPrompt || '');
  const [promptChanged, setPromptChanged] = useState(false);
  const [state, dispatch] = useReducer(reducer, { status: statuses.entry });
  const [images, setImages] = useState([]);
  const [serverError, setServerError] = useState(undefined);
  const [detectedBrandName, setDetectedBrandName] = useState(null);

  const onSelectImage = useCallback((index) => {
    const newImages = [...images];
    newImages[index].isSelected = !newImages[index].isSelected;

    log('AIGenerateImagesView: select image');
    setImages(newImages);
  }, [setImages, images, log]);

  const onStartOver = useCallback(() => {
    if (logger && logger.logTrace) {
      logger.logTrace('AIgeneratedImage: start over button clicked');
    }
    setPrompt(initialPrompt || '');
  }, [initialPrompt, logger]);

  const selectedImages = useMemo(() => images.filter(x => x.isSelected), [images]);

  const generateImagesPromise = useRef(undefined);

  const getGenerateImagesByAIPromise = useCallback((skipBrandDetection) => {
    setImages([]);
    let cancelled = false;
    let statusPoller = null;
    const aiGCImagePerfMarker = perfMarker.createChild('AIGenerateImages');
    aiGCImagePerfMarker.willInit();
    const onStartPolling = (poller) => {
      statusPoller = poller;
    };
    const aigcCallStart = Date.now();

    dataService.generateImagesByAI({
      prompt, onStartPolling /* , onProgress */, skipBrandDetection,
    }).then(({
      images: newImages, brandNameDetected, brandNames,
    }) => {
      const aigcCallEnd = Date.now();
      const aigcCallDuration = aigcCallEnd - aigcCallStart;
      const hasRecraftImage = newImages.some(({ modelSource }) => modelSource === AIModelSource.Recraft);

      // use the tag "logForExpand" to expand the log to per image Url without expanding other logs with imageUrls
      log('AIGenerateImagesView: count AI generated images', {
        imageUrls: newImages.map(({ url }) => url).join(','),
        logForExpand: true,
        skipBrandDetection,
        prompt,
        brandNameDetected: !!brandNameDetected,
        hasRecraftImage,
        modelSources: newImages.map(({ modelSource }) => modelSource).join(','),
        duration: aigcCallDuration,
      }); // will expand the log to per image Url
      if (eventHandler && eventHandler.onReturnImages) {
        eventHandler.onReturnImages({ imageIds: newImages.map(({ id }) => id) });
      }

      if (!cancelled) {
        if (brandNameDetected) {
          setDetectedBrandName(brandNames);
          log(`AIGenerateImagesView: detected brand names: ${brandNames}, prompt: ${prompt}`);
        } else {
          setImages(newImages.map(({ url, modelSource }) => ({ thumbnailSrc: url, modelSource })));
          dispatch({ type: actions.finish });
        }
      }
    }).catch((error) => {
      /* istanbul ignore next */
      if (!cancelled) {
        dispatch({ type: actions.fail });
        log('AIGenerateImagesView: generated images falied', { error });
        if (error && error.uxMessage) {
          setServerError(i18n.getString(error.uxMessage));
        } else {
          setServerError(i18n.getString(_TL_('We\'ve encountered an error and failed to generate images.')));
        }
      }
    }).finally(() => {
      aiGCImagePerfMarker.done();
    });

    return ({
      cancel: () => {
        cancelled = true;
        if (statusPoller && statusPoller.stop) {
          statusPoller.stop();
          statusPoller = null;
        }
      },
    });
  }, [perfMarker, dataService, prompt, log, eventHandler, i18n]);

  const getImages = useCallback((skipBrandDetection = false) => {
    dispatch({ type: actions.search });
    clearExample();
    setServerError(undefined);
    log('AIGenerateImagesView: generate image', { prompt });
    if (eventHandler && eventHandler.onGenerateImages) {
      eventHandler.onGenerateImages({ prompt });
    }
    generateImagesPromise.current = getGenerateImagesByAIPromise(skipBrandDetection);
  }, [clearExample, log, prompt, eventHandler, getGenerateImagesByAIPromise]);

  /* istanbul ignore next */
  const cancel = useCallback(() => {
    setDetectedBrandName(null);
    dispatch({ type: actions.cancel });
    log('AIGenerateImagesView: cancel');
    if (generateImagesPromise.current) {
      generateImagesPromise.current.cancel();
      generateImagesPromise.current = null;
    }
  }, [log]);

  useEffect(() => {
    if (state.status === statuses.cancelling) {
      dispatch({ type: actions.finish });
    }
  }, [state.status]);

  useEffect(() => {
    if (serverError != null) {
      setServerError(undefined);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prompt]);

  const isValid = useMemo(() => {
    let containsBrand = false;
    if (detectedBrandName) {
      // multiple brand names are separated by comma
      // replace special characters in brand names and split by comma
      containsBrand = detectBrandName(detectedBrandName, prompt);
    }
    const trimmedPrompt = prompt ? prompt.trim() : '';
    const isValidPrompt = prompt != null && prompt.length > 0 && trimmedPrompt.length > 0 && prompt.length <= 256;
    return isValidPrompt && !containsBrand;
  }, [detectedBrandName, prompt]);

  const promptError = useMemo(() => {
    if (!promptChanged) {
      return undefined;
    }
    if (prompt == null) {
      return undefined;
    }
    if (prompt.length === 0) {
      return i18n.getString(_TL_('Prompt is required'));
    }
    if (prompt.length > 256) {
      return i18n.getString(_TL_('Prompt must be less than 256 characters'));
    }
    return undefined;
  }, [promptChanged, prompt, i18n]);

  const [savedAIGCImages, setSavedAIGCImages] = useState([]);
  const savedAIGCImagesRef = useRef([]);
  const [savingAIGCImages, setSavingAIGCImages] = useState([]);
  const [savedStockImages, setSavedStockImages] = useState([]);
  const savedStockImagesRef = useRef([]);
  const [savingStockImages, setSavingStockImages] = useState([]);

  const {
    assets,
    loadData,
    loadMoreData,
    totalAssetCount,
    addAIGCImage,
    addStockImage,
  } = dataService;

  const loadStockImages = useCallback(() => {
    loadData({
      searchText: prompt,
    });
  }, [loadData, prompt]);

  const loadMoreStockImages = useCallback(() => {
    loadMoreData();
  }, [loadMoreData]);

  /* istanbul ignore next */
  const handleError = useCallback((err) => {
    const errorCode = _.get(err, ['body', 'value', 0, 'Code'], '');
    if (errorCode === 'ImagesLimitExceededPerAccount') {
      const assetLimit = getImageAssetLimitPerAccount(aid, logger);
      notificationApi.showWarning(i18n.getString(_TL_('One or more images failed to upload because your account has exceeded the {{value}}-image asset limit. Please remove assets and try again.'), { value: assetLimit }));
    } else {
      const learnMoreText = i18n.getString(_TL_('Learn more about asset guidelines and accepted file types.'));
      const topic = 'app54863';
      const learnMoreLink = `<a class="HelpTopic" href="javascript:void(0);" query="help:${topic}"
         aria-label="${learnMoreText}" role="button">Learn more</a>`;
      const genericError = i18n.getString(_TL_('One or more assets failed to upload. Please try again. {{learnMoreLink}}'), { learnMoreLink });
      notificationApi.showWarning(genericError);
    }
  }, [aid, i18n, logger, notificationApi]);

  const folderIdNumber = useMemo(() => {
    if (folderId && folderId !== ROOT_FOLDER_ID) {
      return Number(folderId);
    }
    return undefined;
  }, [folderId]);

  const wrappedAddAIGCImage = useCallback((imageSrc, modelSource = null) => {
    const aiGCImagePerfMarker = perfMarker.createChild('AIGCImagePicker');
    aiGCImagePerfMarker.willInit();
    setSavingAIGCImages([...savingAIGCImages, imageSrc]);
    addAIGCImage(imageSrc, folderIdNumber, modelSource).then((res) => {
      if (logger && logger.logTrace) {
        logger.logTrace('AIGC image saved', {
          imageSrc,
          id: res,
          prompt,
          modelSource: modelSource || AIModelSource.Dalle,
        });
      }
      savedAIGCImagesRef.current = [...new Set([...savedAIGCImagesRef.current, imageSrc])];
      setSavedAIGCImages([...new Set([...savedAIGCImagesRef.current, ...savedAIGCImages, imageSrc])]);
    }).catch((error) => {
      handleError(error);
    }).finally(() => {
      aiGCImagePerfMarker.done();
    });
  }, [addAIGCImage, folderIdNumber, handleError, logger, perfMarker, savedAIGCImages, savingAIGCImages, prompt]);

  const wrappedAddStockImage = useCallback((stockImageId) => {
    setSavingStockImages([...savingStockImages, stockImageId]);
    addStockImage(stockImageId, folderIdNumber).then((res) => {
      if (logger && logger.logTrace) {
        logger.logTrace('Stock image saved', { stockImageId, id: res, prompt });
      }
      savedStockImagesRef.current = [...new Set([...savedStockImagesRef.current, stockImageId])];
      setSavedStockImages([...new Set([...savedStockImagesRef.current, ...savedStockImages, stockImageId])]);
    }).catch((error) => {
      handleError(error);
    });
  }, [addStockImage, folderIdNumber, handleError, logger, savedStockImages, savingStockImages, prompt]);

  const wrappedSetPrompt = useCallback((newPrompt) => {
    if (promptChanged === false) {
      setPromptChanged(true);
    }
    setPrompt(newPrompt);
  }, [promptChanged]);

  return {
    aigcImages: images,
    stockImages: assets,
    savedAIGCImages,
    savedStockImages,
    savingAIGCImages,
    savingStockImages,
    totalStockImagesCount: totalAssetCount,
    addAIGCImage: wrappedAddAIGCImage,
    addStockImage: wrappedAddStockImage,
    selectedImages,
    isValid,
    getImages,
    onSelectImage,
    prompt,
    promptError,
    setPrompt: wrappedSetPrompt,
    state,
    cancel,
    serverError,
    detectedBrandName,
    loadStockImages,
    loadMoreStockImages,
    setDetectedBrandName,
    onStartOver,
    generateImageExample,
  };
};
